import { useMutation, useQueryClient } from '@tanstack/react-query';

import { fbmSubstitutionRulesV1Api } from 'api/fbmSubstitutionRulesV1';
import { ApiError } from 'shared/errors/ApiError';

import { substitutionRuleEventsQueryKeys } from './useSubstitutionRuleEventsQuery';
import { substitutionRulesQueryKeys } from './useSubstitutionRulesQuery';

/**
 * Deletes a substitution rule group; on success invalidates the rules list.
 *
 * @param {Omit<
 *   import('@tanstack/react-query').UseMutationOptions<
 *     Object,
 *     ApiError,
 *     { fbmAccountName: string, facilityId: number, ruleGroupId: string }
 *   >,
 *   'mutationFn'
 * >} [mutationOptions={}]
 * @returns {import('@tanstack/react-query').UseMutationResult<
 *   Object,
 *   ApiError,
 *   { fbmAccountName: string, facilityId: number, ruleGroupId: string }
 * >}
 */
export function useDeleteRuleGroupMutation(mutationOptions = {}) {
    const queryClient = useQueryClient();
    const { onSuccess, ...rest } = mutationOptions;

    return useMutation({
        mutationFn: async ({ fbmAccountName, ruleGroupId }) => {
            const response = await fbmSubstitutionRulesV1Api.deleteRuleGroup(fbmAccountName, ruleGroupId);
            if (!response?.status) {
                throw new ApiError(
                    response?.msg ?? 'Failed to delete substitution rule',
                    response?.statusCode,
                    response?.data,
                );
            }
            return response.data;
        },
        onSuccess: (data, variables, ...args) => {
            queryClient.invalidateQueries({
                queryKey: substitutionRulesQueryKeys.account(variables.fbmAccountName),
            });
            queryClient.invalidateQueries({
                queryKey: substitutionRuleEventsQueryKeys.account(variables.fbmAccountName),
            });
            return onSuccess?.(data, variables, ...args);
        },
        ...rest,
    });
}
